import React, { useState } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Swal from "sweetalert2";
import { useMutation } from "@apollo/client";
import { CHANGE_PASSWORD } from "../../data/userData";

const defaultTheme = createTheme();

// ---------- Validation helpers ----------

const hasWhitespace = (value) => /\s/.test(value);

const validateOldPassword = (value) => {
  if (!value) return "Please enter your old password.";
  if (hasWhitespace(value)) return "Password cannot contain spaces.";
  return "";
};

const validateNewPassword = (value, oldValue = "") => {
  if (!value) return "Please enter a new password.";
  if (hasWhitespace(value)) return "Password cannot contain spaces.";
  if (value.length < 6) return "Password must be at least 6 characters long.";
  if (!/[a-z]/.test(value)) return "Password must include at least one lowercase letter.";
  if (!/[A-Z]/.test(value)) return "Password must include at least one uppercase letter.";
  if (!/[\W_]/.test(value)) return "Password must include at least one special character.";
  if (oldValue && value === oldValue) return "New password must be different from the old password.";
  return "";
};

// ---------- Response / error helpers ----------

// Maps the server's response to a field error or a general message.
// Returns { ok: true } on success, otherwise { ok: false, field?, message }.
const interpretResponse = (result) => {
  if (!result) {
    return { ok: false, message: "No response from the server. Please try again." };
  }

  const { hashedPassword, isValid, failReason } = result;

  if (hashedPassword === "Invalid") {
    return {
      ok: false,
      field: "oldPassword",
      message: "The old password you entered is incorrect.",
    };
  }
  if (hashedPassword === "Unauthorised") {
    return {
      ok: false,
      fatal: true,
      message: "Your session has expired or you are not authorised. Please log in again.",
    };
  }
  if (hashedPassword === "Failed") {
    return { ok: false, message: "We could not change your password. Please try again later." };
  }
  if (isValid !== undefined && isValid !== null && Number(isValid) !== 1) {
    const reason = failReason || "";
    // Surface "incorrect old password" style reasons on the right field
    if (/old|current|incorrect|invalid|wrong/i.test(reason)) {
      return {
        ok: false,
        field: "oldPassword",
        message: "The old password you entered is incorrect.",
      };
    }
    return {
      ok: false,
      message: reason ? `Password change failed: ${reason}` : "Password change failed.",
    };
  }
  return { ok: true };
};

const describeNetworkError = (error) => {
  if (error?.networkError) {
    return "Unable to reach the server. Please check your internet connection and try again.";
  }
  const gqlMessage = error?.graphQLErrors?.[0]?.message;
  if (gqlMessage) return gqlMessage;
  return "Something went wrong while changing your password. Please try again later.";
};

const getUsernameFromToken = () => {
  try {
    const decoded = JSON.parse(localStorage.getItem("decodedToken"));
    return decoded?.Username || null;
  } catch {
    return null;
  }
};

// ---------- Component ----------

export default function ChangePasswordForm() {
  const navigate = useNavigate();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [oldPasswordError, setOldPasswordError] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [formError, setFormError] = useState("");

  const [changePasswordMutation, { loading }] = useMutation(CHANGE_PASSWORD);

  const handleOldPasswordChange = (event) => {
    const value = event.target.value;
    setOldPassword(value);
    setFormError("");
    setOldPasswordError(value ? validateOldPassword(value) : "");
    // Re-check new password since it must differ from the old one
    if (newPassword) setNewPasswordError(validateNewPassword(newPassword, value));
  };

  const handleNewPasswordChange = (event) => {
    const value = event.target.value;
    setNewPassword(value);
    setFormError("");
    setNewPasswordError(value ? validateNewPassword(value, oldPassword) : "");
  };

  // Block spaces as they are typed/pasted-in via keyboard
  const blockSpaceKey = (event) => {
    if (event.key === " ") {
      event.preventDefault();
      const setter = event.target.name === "oldPassword" ? setOldPasswordError : setNewPasswordError;
      setter("Spaces are not allowed in passwords.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    // Validate everything before calling the server
    const oldErr = validateOldPassword(oldPassword);
    const newErr = validateNewPassword(newPassword, oldPassword);
    setOldPasswordError(oldErr);
    setNewPasswordError(newErr);
    if (oldErr || newErr) return;

    const username = getUsernameFromToken();
    if (!username) {
      await Swal.fire({
        title: "Session expired",
        text: "We couldn't verify your login. Please log in again.",
        icon: "warning",
        confirmButtonText: "Go to login",
      });
      navigate("/login");
      return;
    }

    try {
      const { data } = await changePasswordMutation({
        variables: { identifier: username, oldPassword, password: newPassword },
      });

      const outcome = interpretResponse(data?.changePassword);

      if (outcome.ok) {
        await Swal.fire({
          title: "Success",
          text: "Password changed successfully! Please log in with your new password.",
          icon: "success",
          confirmButtonText: "OK",
        });
        navigate("/login");
        return;
      }

      if (outcome.fatal) {
        await Swal.fire({
          title: "Not authorised",
          text: outcome.message,
          icon: "error",
          confirmButtonText: "Go to login",
        });
        navigate("/login");
        return;
      }

      if (outcome.field === "oldPassword") {
        setOldPasswordError(outcome.message);
        setOldPassword(""); // let the user retype it
      } else {
        setFormError(outcome.message);
      }
    } catch (error) {
      console.error("Change password error:", error);
      setFormError(describeNetworkError(error));
    }
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Grid container component="main" sx={{ height: "100vh" }}>
        <CssBaseline />
        <Grid
          item
          xs={false}
          sm={4}
          md={7}
          sx={{
            backgroundImage: "url(/path/to/image.jpg)",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
          <Box sx={{ my: 8, mx: 4, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              Change Password
            </Typography>

            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1, width: "100%" }}>
              {formError && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setFormError("")}>
                  {formError}
                </Alert>
              )}

              <TextField
                margin="normal"
                required
                fullWidth
                id="oldPassword"
                label="Old Password"
                name="oldPassword"
                type={showOldPassword ? "text" : "password"}
                autoComplete="current-password"
                value={oldPassword}
                onChange={handleOldPasswordChange}
                onKeyDown={blockSpaceKey}
                error={!!oldPasswordError}
                helperText={oldPasswordError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle old password visibility"
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        edge="end"
                      >
                        {showOldPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                id="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={handleNewPasswordChange}
                onKeyDown={blockSpaceKey}
                error={!!newPasswordError}
                helperText={
                  newPasswordError ||
                  "At least 6 characters with upper & lowercase letters and a special character. No spaces."
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle new password visibility"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || !!oldPasswordError || !!newPasswordError}
              >
                {loading ? "Changing Password..." : "Change Password"}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}